import { ApiRoutes } from "../../../../Constants/ApiRoutingConstants";
import axios, { AxiosInstance } from "axios";
import authorizeService from "../../../Identity/Utilities/AuthorizeService";
import HealthMetricsReadDto from "../../../../Models/Dashboard/HealthMetricsReadDto";
import { EventFilter } from "../../../Events/Utilities/EventsApiService";
import DeviceInfoReadDto from "../../../../Models/Dashboard/DeviceInfoReadDto";
import DeviceNotificationsReadDto from "../../../../Models/Dashboard/DeviceNotificationsReadDto";
import DeviceEventsFlowReadDto from "../../../../Models/Dashboard/DeviceEventsFlowReadDto";
import DeviceEventsButtonReadDto from "../../../../Models/Dashboard/DeviceEventsButtonReadDto";
import DeviceEventsTemperatureReadDto from "../../../../Models/Dashboard/DeviceEventsTemperatureReadDto";
import DeviceEventsPressureReadDto from "../../../../Models/Dashboard/DeviceEventsPressureReadDto";
import DeviceEventsHeartBeatReadDto from "../../../../Models/Dashboard/DeviceEventsHeartBeatReadDto";
import DeviceEventsDiagnosticsReadDto from "../../../../Models/Dashboard/DeviceEventsDiagnosticsReadDto";
import HeadUnitsFlowEventsReadDto from "../../../../Models/Dashboard/HeadUnitsFlowEventsReadDto";
import DeviceFeaturesReadDto from "../../../../Models/Dashboard/DeviceFeaturesReadDto";


/**
 * This service provides implementations for http calls to the api pertaining to event crud functionality
 */

class HealthDashboardApiService {
  axiosInstance: AxiosInstance;
  constructor() {
    //Subscribe to identity store the get token
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_EVENT_SERVICE_URL,
    });
  }

  async getHealthDashboardMetrics(): Promise<HealthMetricsReadDto> {
    const response = await this.axiosInstance({
      method: "GET",
      //@ts-ignore
      url: `${ApiRoutes.Dashboards}/health`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    })
    return response.data;
  }

  async getDeviceInfoChart(eventFilters: EventFilter, serialNumber: string): Promise<DeviceInfoReadDto> {
    eventFilters.endDate?.setHours(23,59,59)
    eventFilters.startDate?.setHours(0,0,0)
    //Covnert EventFilters to use Iso date strings
    const isoEventFilters = {...eventFilters, 
      startDate : eventFilters.startDate?.toISOString(),
      endDate : eventFilters.endDate?.toISOString()}

    const response = await this.axiosInstance({
      method: "GET",
      // @ts-ignore
      url: `${ApiRoutes.DeviceInfo}/${serialNumber}?${new URLSearchParams(isoEventFilters).toString()}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });

    return response.data;
  }

  async getDeviceFlowEvents(serialNumber: string, eventFilters?: EventFilter): Promise<DeviceEventsFlowReadDto[]> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/endpoint/events/flow/device/${serialNumber}${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getDeviceButtonEvents(serialNumber: string, eventFilters?: EventFilter): Promise<DeviceEventsButtonReadDto[]> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsV1}/events/button/device/${serialNumber}${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getDeviceTemperatureEvents(serialNumber: string, eventFilters?: EventFilter): Promise<DeviceEventsTemperatureReadDto[]> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsV1}/events/temperature/device/${serialNumber}${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getDevicePressureEvents(serialNumber: string, eventFilters?: EventFilter): Promise<DeviceEventsPressureReadDto[]> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsV1}/events/pressure/device/${serialNumber}${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getDeviceHeartbeatEvents(serialNumber: string, eventFilters?: EventFilter): Promise<DeviceEventsHeartBeatReadDto[]> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsV1}/events/heartbeat/device/${serialNumber}${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getDeviceDiagnosticEvents(serialNumber: string, eventFilters?: EventFilter): Promise<DeviceEventsDiagnosticsReadDto[]> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsV1}/events/diagnostic/device/${serialNumber}${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getDeviceNotificationsChart(serialNumber: string, eventFilters?: EventFilter): Promise<DeviceNotificationsReadDto> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.Notifications}/devices/${serialNumber}/chart${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getDeviceFeatures(serialNumber: string): Promise<DeviceFeaturesReadDto> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/devices/endpoints/features/${serialNumber}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getHeadUnitFlowEvents(serialNumber: string, eventFilters?: EventFilter): Promise<HeadUnitsFlowEventsReadDto[]> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.DevicesV1}/head-units/${serialNumber}/events/flow${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getDeviceStateEvents(serialNumber: string, eventFilters?: EventFilter): Promise<any[]> {
    const params = eventFilters ? this.buildEventFilterParams(eventFilters) : '';
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.DevicesV1}/events/state/${serialNumber}${params}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  private buildEventFilterParams(eventFilters: EventFilter): string {
    eventFilters.endDate?.setHours(23, 59, 59);
    eventFilters.startDate?.setHours(0, 0, 0);
    
    const isoEventFilters: Record<string, string> = {};
    
    // Only add defined values to avoid undefined in URLSearchParams
    if (eventFilters.startDate) {
      isoEventFilters.startDate = eventFilters.startDate.toISOString();
    }
    if (eventFilters.endDate) {
      isoEventFilters.endDate = eventFilters.endDate.toISOString();
    }
    
    // Add other event filter properties if they exist
    Object.entries(eventFilters).forEach(([key, value]) => {
      if (key !== 'startDate' && key !== 'endDate' && value !== undefined && value !== null) {
        isoEventFilters[key] = String(value);
      }
    });
    
    return `?${new URLSearchParams(isoEventFilters).toString()}`;
  }

}

const healthDashboardApiService = new HealthDashboardApiService();
export default healthDashboardApiService;
